import React, { useState, useEffect } from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { diagnosticService, adminService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Tenant } from '../../types';
import '../../reports.css';

interface TestResultViewProps {
  result: any;
  onBack: () => void;
  skipApiCall?: boolean;
}

const TestResultView: React.FC<TestResultViewProps> = ({ result, onBack, skipApiCall = false }) => {
  const [resultDetails, setResultDetails] = useState<any>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [testOrder, setTestOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const extractDetailsFromTests = (resultData: any) => {
    console.log('DEBUG: extractDetailsFromTests called with resultData:', resultData);
    let detailsList: any[] = [];
    
    if (resultData.tests && Array.isArray(resultData.tests) && resultData.tests.length > 0) {
      console.log('DEBUG: Inside extraction condition, tests.length:', resultData.tests.length);
      for (let i = 0; i < resultData.tests.length; i++) {
        const test = resultData.tests[i];
        console.log(`DEBUG: Processing test ${i}:`, test);
        if (test.parameters && Array.isArray(test.parameters) && test.parameters.length > 0) {
          console.log(`DEBUG: Test ${i} has ${test.parameters.length} parameters`);
          for (let j = 0; j < test.parameters.length; j++) {
            const param = test.parameters[j];
            detailsList.push({
              parameter_id: param.parameter_id,
              parameter_name: param.parameter_name,
              unit: param.unit,
              parameter_value: param.parameter_value,
              reference_value: param.reference_value,
              verdict: param.verdict,
              notes: param.notes,
              method_name: param.method_name,
              testName: test.name
            });
          }
        }
      }
    }
    
    console.log('DEBUG: Extracted detailsList:', detailsList);
    return detailsList;
  };

  useEffect(() => {
    console.log('DEBUG: useEffect - skipApiCall:', skipApiCall, 'result.id:', result.id);
    if (!skipApiCall) {
      console.log('DEBUG: Calling loadResultDetails()');
      loadResultDetails();
    } else {
      console.log('DEBUG: skipApiCall is true, using result prop directly');
      const details = extractDetailsFromTests(result);
      const resultWithDetails = {
        ...result,
        details: details
      };
      setResultDetails(resultWithDetails);
      loadTenantOnly();
    }
  }, [result.id, skipApiCall]);

  useEffect(() => {
    console.log('DEBUG: resultDetails changed:', resultDetails);
    if (resultDetails) {
      console.log('DEBUG: resultDetails.details:', resultDetails.details);
      console.log('DEBUG: resultDetails.details length:', resultDetails.details?.length);
      console.log('DEBUG: resultDetails.tests:', resultDetails.tests);
      console.log('DEBUG: resultDetails.tests length:', resultDetails.tests?.length);
    }
  }, [resultDetails]);

  const loadTenantOnly = async () => {
    try {
      const tenantResponse = await adminService.getTenant();
      setTenant(tenantResponse.data);
    } catch (error) {
      showToast('error', 'Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const loadResultDetails = async () => {
    try {
      console.log('DEBUG: loadResultDetails called with result.id:', result.id);
      const [resultResponse, tenantResponse] = await Promise.all([
        diagnosticService.getTestResult(result.id),
        adminService.getTenant()
      ]);
      const resultData = resultResponse.data;
      console.log('DEBUG: Got resultData:', resultData);
      setTenant(tenantResponse.data);
      
      // Extract details using common function
      const detailsList = extractDetailsFromTests(resultData);
      
      // Create updated result with flattened details
      const updatedResult = {
        ...resultData,
        details: detailsList
      };
      
      console.log('DEBUG: Setting resultDetails with details length:', updatedResult.details?.length);
      setResultDetails(updatedResult);
      
      if (resultData.test_order_id) {
        const orderResponse = await diagnosticService.getTestOrder(resultData.test_order_id);
        setTestOrder(orderResponse.data);
      }
    } catch (error) {
      console.error('Error in loadResultDetails:', error);
      showToast('error', 'Failed to load result details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="text-gray-500">Loading result details...</div>
      </div>
    );
  }

  if (!resultDetails) {
    return (
      <div className="p-6">
        <div className="text-red-500">Result not found</div>
        <button onClick={onBack} className="mt-4 text-blue-600 hover:text-blue-800">
          ← Back to Results
        </button>
      </div>
    );
  }

  return (
    <div className="report-view bg-white">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center print:hidden bg-gray-50">
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Test Result Details</h2>
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          <Printer className="h-3 w-3 mr-1" />
          Print
        </button>
      </div>

      <div className="report-content report-container max-w-4xl mx-auto p-8">
        <div className="report-header border-b-2 border-blue-600 pb-3 mb-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h1 className="report-company-name text-3xl font-bold text-blue-600 mb-2">{tenant?.name || 'Company Name'}</h1>
              {tenant?.address && (
                <div className="text-gray-600 mb-2">
                  <div className="whitespace-pre-line">{tenant.address}</div>
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                {tenant?.phone && <div>Phone: {tenant.phone}</div>}
                {tenant?.email && <div>Email: {tenant.email}</div>}
                {tenant?.tax_id && <div>Tax ID: {tenant.tax_id}</div>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {resultDetails.qr_code && (
                <img 
                  src={resultDetails.qr_code} 
                  alt="Test Result QR Code" 
                  className="w-12 h-12"
                />
              )}
              <div className="text-right">
                <h2 className="report-title text-2xl font-bold text-gray-800 mb-2">TEST RESULT</h2>
                <div className="text-sm text-gray-600">
                  <div>Date: {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="report-section grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="report-details bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Result Information</h3>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Result Number:</span>
                <span className="font-semibold text-gray-900">{resultDetails.result_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Result Date:</span>
                <span className="text-gray-900">{new Date(resultDetails.result_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: '2-digit' 
                })}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Result Type:</span>
                <span className="text-gray-900">{resultDetails.result_type}</span>
              </div>
            </div>
          </div>
          
          <div className="report-details bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Performed By</h3>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Name:</span>
                <span className="font-semibold text-gray-900">{resultDetails.performed_by || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">License:</span>
                <span className="text-gray-900">{resultDetails.license_number || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {(resultDetails.overall_report || resultDetails.notes) && (
          <div className="report-section grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {resultDetails.overall_report && (
              <div className="report-details bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Overall Report</h3>
                <p className="text-sm text-gray-700">{resultDetails.overall_report}</p>
              </div>
            )}
            {resultDetails.notes && (
              <div className="report-details bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Notes</h3>
                <p className="text-sm text-gray-700">{resultDetails.notes}</p>
              </div>
            )}
          </div>
        )}

        {resultDetails.details && resultDetails.details.length > 0 ? (
          <div className="report-section mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Parameter Details</h3>
            <div className="overflow-x-auto shadow-sm border border-gray-200 rounded-lg">
              <table className="report-table w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Parameter Name</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Value</th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Unit</th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Reference</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const grouped: {[testName: string]: any[]} = {};
                    if (resultDetails.details && Array.isArray(resultDetails.details)) {
                      resultDetails.details.forEach((detail: any) => {
                        const testName = detail.testName || 'Other';
                        if (!grouped[testName]) grouped[testName] = [];
                        grouped[testName].push(detail);
                      });
                    }
                    
                    return Object.entries(grouped).map(([testName, testDetails]) => (
                      <React.Fragment key={testName}>
                        <tr className="bg-blue-100">
                          <td colSpan={6} className="px-3 py-2 text-sm font-bold text-blue-900 border-b">
                            {testName}
                          </td>
                        </tr>
                        {testDetails.map((detail: any, index: number) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-2 py-2 text-sm border-b">
                              <div className="font-medium text-gray-900">{detail.parameter_name}</div>
                              {detail.method_name && <div className="text-xs text-gray-500 mt-0.5">{detail.method_name}</div>}
                            </td>
                            <td className="px-2 py-2 text-sm text-right font-medium text-gray-900 border-b">{detail.parameter_value}</td>
                            <td className="px-2 py-2 text-sm text-center text-gray-700 border-b">{detail.unit || '-'}</td>
                            <td className="px-2 py-2 text-sm text-center text-gray-700 border-b">{detail.reference_value}</td>
                            <td className="px-2 py-2 text-sm text-gray-700 border-b">{detail.notes || '-'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="report-section mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800 mb-2">No parameter details available.</p>
            <div className="text-xs text-yellow-700 space-y-1">
              <p>Details: {JSON.stringify(resultDetails.details)}</p>
              <p>Tests: {Array.isArray(resultDetails.tests) ? resultDetails.tests.length + ' found' : 'not found'}</p>
              {resultDetails.tests && Array.isArray(resultDetails.tests) && (
                <details className="mt-2">
                  <summary className="cursor-pointer underline">View raw tests data</summary>
                  <pre className="mt-2 bg-white p-2 overflow-auto max-h-64 text-xs">
                    {JSON.stringify(resultDetails.tests, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )}

        {resultDetails.files && resultDetails.files.length > 0 && (
          <div className="report-section mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-1">Image Details</h3>
            <div className="overflow-x-auto shadow-sm border border-gray-200 rounded-lg">
              <table className="report-table w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">File Name</th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Format</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Size (bytes)</th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Acquisition Date</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resultDetails.files.map((file: any, index: number) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 py-2 text-sm font-medium text-gray-900 border-b">{file.file_name}</td>
                      <td className="px-2 py-2 text-sm text-center text-gray-700 border-b">{file.file_format}</td>
                      <td className="px-2 py-2 text-sm text-right text-gray-700 border-b">{file.file_size}</td>
                      <td className="px-2 py-2 text-sm text-center text-gray-700 border-b">{new Date(file.acquisition_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: '2-digit' 
                      })}</td>
                      <td className="px-2 py-2 text-sm text-gray-700 border-b">{file.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="report-footer border-t border-gray-200 pt-3 mt-4">
          <div className="text-right">
            <div className="report-signature mb-8">
              <p className="text-xs text-gray-600 mb-1">Authorized Signature</p>
              <div className="border-b border-gray-300 w-32 ml-auto"></div>
            </div>
            <div className="text-xs text-gray-500">
              <p>This is a computer generated document.</p>
              <p>Generated on: {new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResultView;
